// src/components/ProgressoAvaliacao.js

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, auth } from '../../firebaseConfig';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, writeBatch, addDoc, Timestamp } from 'firebase/firestore';
import Select from 'react-select';
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';
import '../../styles/ProgressoAvaliacao.css';
import { FaPaperPlane, FaClock, FaCalendarCheck, FaDownload, FaCheckCircle, FaUser, FaUserTie, FaUsers, FaSitemap } from 'react-icons/fa';

// --- ESTILOS DO REACT-SELECT (White-Label) ---
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: '#fff',
    borderColor: state.isFocused ? 'var(--primary-color, #007bff)' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 1px var(--primary-color, #007bff)' : 'none',
    '&:hover': {
      borderColor: 'var(--primary-color, #007bff)',
    },
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: '8px',
    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
    backgroundColor: '#fff',
    zIndex: 9999
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? 'var(--primary-color, #007bff)'
      : state.isFocused
      ? '#e0f2ff'
      : '#fff',
    color: state.isSelected
      ? '#fff'
      : '#212529',
    cursor: 'pointer'
  }),
  placeholder: (provided) => ({ ...provided, color: '#6c757d' }),
  singleValue: (provided) => ({ ...provided, color: '#212529' }),
};

// --- COMPONENTES AUXILIARES ---

const KPI = ({ icon, label, value, unit }) => (
    <div className="kpi-card">
        <div className="kpi-icon"><i className={`fas ${icon}`}></i></div>
        <div className="kpi-content">
            <span className="kpi-value">{value}{unit}</span>
            <span className="kpi-label">{label}</span>
        </div>
    </div>
);

const NineBoxPreview = ({ data, onExpand }) => {
    const colorGrid = [
        ['#FDE2E2', '#FFF5D8', '#E3F5E9'],
        ['#FDC4C4', '#FFE7AD', '#B9E8C7'],
        ['#F8A0A0', '#FFD981', '#87D7A3']
    ];
    const gridCounts = useMemo(() => {
        const counts = Array(3).fill(null).map(() => Array(3).fill(0));
        if (data) {
            data.forEach(user => {
                if (user.potentialCategoryIndex !== undefined && user.performanceCategoryIndex !== undefined) {
                    counts[user.potentialCategoryIndex][user.performanceCategoryIndex]++;
                }
            });
        }
        return counts;
    }, [data]);

    return (
        <div className="nine-box-preview">
            <div className="preview-header">
                <h4>Matriz 9-Box</h4>
                <button onClick={onExpand} className="btn-link">Ver Detalhes <i className="fas fa-expand-alt"></i></button>
            </div>
            <div className="preview-main-content">
                <div className="preview-y-axis"><span>Potencial</span></div>
                <div className="preview-grid-container">
                    <div className="preview-grid">
                        {gridCounts.slice().reverse().map((row, pIndex) => (
                            row.map((count, perfIndex) => (
                                <div
                                    key={`${2 - pIndex}-${perfIndex}`}
                                    className="preview-cell"
                                    style={{ backgroundColor: colorGrid[2 - pIndex][perfIndex] }}
                                    title={`${count} pessoa(s)`}
                                >
                                    <span className="preview-cell-count">{count}</span>
                                </div>
                            ))
                        ))}
                    </div>
                    <div className="preview-x-axis"><span>Desempenho</span></div>
                </div>
            </div>
        </div>
    );
};

const QuestionAnalytics = ({ questions, averages, baseScale }) => {
    if (!questions || questions.length === 0) return null;

    const getRelationshipLabel = (relationship) => {
        const labels = { self: 'Autoavaliação', manager: 'Gestor', peer: 'Par', direct_report: 'Liderado', other: 'Outro' };
        return labels[relationship] || relationship;
    };

    return (
        <div className="question-analytics-section">
            <div className="section-header">
                <h3><i className="fas fa-chart-bar"></i> Análise por Pergunta</h3>
                <p>Média de notas para cada pergunta, segmentada por tipo de relação.</p>
            </div>
            <div className="question-list">
                {questions.map((q, index) => {
                    const questionAvg = averages[q.questionId];
                    if (!questionAvg) return null;

                    return (
                        <div key={q.questionId} className="question-item">
                            <div className="question-text">
                                <span>{index + 1}. {q.text}</span>
                            </div>
                            <div className="question-averages">
                                {Object.entries(questionAvg.byRelationship).map(([rel, avg]) => (
                                    <div key={rel} className="average-row">
                                        <span className="relationship-label">{getRelationshipLabel(rel)}</span>
                                        <div className="average-bar-container">
                                            <div className="average-bar" style={{ width: `${(avg / baseScale) * 100}%` }}></div>
                                        </div>
                                        <span className="average-value">{avg.toFixed(2)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const NineBoxGrid = ({ data, users, cycleName, onClose }) => {
    const gridRef = useRef(null);
    const [isDownloading, setIsDownloading] = useState(false);
    
    const potentialLabels = ['Baixo', 'Médio', 'Alto'];
    const performanceLabels = ['Abaixo do Esperado', 'Esperado', 'Acima do Esperado'];
    const colorGrid = [
        ['#FDE2E2', '#FFF5D8', '#E3F5E9'],
        ['#FDC4C4', '#FFE7AD', '#B9E8C7'],
        ['#F8A0A0', '#FFD981', '#87D7A3']
    ];

    const grid = useMemo(() => {
        const initialGrid = potentialLabels.map(() => Array(performanceLabels.length).fill(null).map(() => []));
        if (data) {
            data.forEach(user => {
                if (user.potentialCategoryIndex !== undefined && user.performanceCategoryIndex !== undefined) {
                    initialGrid[user.potentialCategoryIndex][user.performanceCategoryIndex].push(user);
                }
            });
        }
        return initialGrid;
    }, [data]);

    const handleDownloadNineBox = () => {
        if (!gridRef.current) return;
        setIsDownloading(true);
        html2canvas(gridRef.current, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff'
        }).then(canvas => {
            const link = document.createElement('a');
            link.download = `Matriz_9Box_${cycleName.replace(/ /g, '_')}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
            setIsDownloading(false);
        }).catch(err => {
            console.error("Erro ao gerar a imagem:", err);
            alert("Ocorreu um erro ao tentar salvar a imagem.");
            setIsDownloading(false);
        });
    };

    return (
         <div className="modal-backdrop" onClick={onClose}>
            <div className="modal-content large" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                     <h3><i className="fas fa-th-large"></i> Matriz 9-Box: Potencial vs. Desempenho</h3>
                     <div>
                        <button onClick={handleDownloadNineBox} className="btn btn-secondary" disabled={isDownloading}>
                            <FaDownload /> {isDownloading ? 'Salvando...' : 'Salvar como PNG'}
                        </button>
                        <button onClick={onClose} className="close-button">&times;</button>
                     </div>
                </div>
                <div className="modal-body">
                    <div ref={gridRef} className="nine-box-printable-area">
                        <p className="nine-box-subtitle">Posicionamento baseado na avaliação do gestor. Na ausência, usa a autoavaliação (indicado com borda pontilhada).</p>
                        <div className="nine-box-container">
                            <div className="y-axis-label">Potencial</div>
                            <div className="nine-box-grid-area">
                                <div className="nine-box-grid">
                                    {grid.slice().reverse().map((row, pIndex) => (
                                        row.map((cell, perfIndex) => (
                                            <div key={`${2-pIndex}-${perfIndex}`} className="nine-box-cell" style={{ backgroundColor: colorGrid[2 - pIndex][perfIndex] }}>
                                                <div className="cell-header">
                                                    {potentialLabels[2 - pIndex]} / {performanceLabels[perfIndex]}
                                                    <span className="cell-count">{cell.length}</span>
                                                </div>
                                                <div className="cell-users">
                                                    {cell.map(user => (
                                                        <div key={user.userId}
                                                            className={`user-capsule ${user.source === 'self' ? 'self-sourced' : ''}`}
                                                            title={`${users[user.userId]?.name} (Desempenho: ${user.performanceScore}, Potencial: ${user.potentialScore})`}>
                                                            {users[user.userId]?.photoURL ?
                                                                <img src={users[user.userId]?.photoURL} alt={users[user.userId]?.name} /> :
                                                                <span>{users[user.userId]?.name?.charAt(0) || '?'}</span>
                                                            }
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))
                                    ))}
                                </div>
                                <div className="x-axis-label">Desempenho</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- COMPONENTE PRINCIPAL ---

const ProgressoAvaliacao = () => {
    const { cycleId } = useParams();
    const navigate = useNavigate();

    // Estados Globais
    const [cycle, setCycle] = useState(null);
    const [responses, setResponses] = useState([]);
    const [participants, setParticipants] = useState({});
    const [users, setUsers] = useState({});
    const [scalesDetails, setScalesDetails] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [adminEmpresaId, setAdminEmpresaId] = useState(null);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [currentUserPermissions, setCurrentUserPermissions] = useState({ role: 'user', viewableUserIds: null });

    // Estados de Filtro
    const [filterEvaluated, setFilterEvaluated] = useState(null);
    const [filterEvaluator, setFilterEvaluator] = useState(null);
    const [filterStatus, setFilterStatus] = useState(null);
    const [filterDepartment, setFilterDepartment] = useState(null);
    const [filterFinalScoreStatus, setFilterFinalScoreStatus] = useState({ value: 'all', label: 'Status da Nota (Todos)' });

    // Outros estados
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isNineBoxModalOpen, setIsNineBoxModalOpen] = useState(false);
    const [selectedResponseData, setSelectedResponseData] = useState(null);
    const [calibrationScores, setCalibrationScores] = useState({});
    const [isExporting, setIsExporting] = useState(false);
    const [isCalibrating, setIsCalibrating] = useState(false);
    const [calibratedAnswers, setCalibratedAnswers] = useState({});
    const [viewMode, setViewMode] = useState('card');
    const [showReminderScheduler, setShowReminderScheduler] = useState(false);
    const [reminderDateTime, setReminderDateTime] = useState('');
    const [isSubmittingReminder, setIsSubmittingReminder] = useState(false);
    const [reminderPanelMessage, setReminderPanelMessage] = useState(null);

    // --- FUNÇÕES DE CÁLCULO E BUSCA ---

    const calculateWeightedScore = useCallback((answers, form, scales, baseScale = 10, options = {}) => {
        const { axis = null, useOriginal = false } = options;
        if (!answers || !form || !scales || Object.keys(scales).length === 0 || Object.keys(answers).length === 0) return "N/A";
        
        let totalNormalizedScore = 0;
        let totalWeight = 0;
        
        const questionsToConsider = axis ? form.filter(q => q.axis === axis) : form;
        
        questionsToConsider.forEach((question) => {
            const answer = answers[question.questionId];
            const weight = parseFloat(question.weight);
            const scale = scales[question.type];
            
            if (scale && !isNaN(weight) && weight > 0 && answer) {
                const scoreToUse = (useOriginal || answer.calibratedScore === undefined) ? answer.score : answer.calibratedScore;
                
                if (scoreToUse !== undefined) {
                    const score = parseFloat(scoreToUse);
                    const maxScore = scale.max;
                    
                    if (!isNaN(score)) {
                        const normalizedScore = maxScore > 0 ? (score / maxScore) * baseScale : 0;
                        totalNormalizedScore += normalizedScore * weight;
                        totalWeight += weight;
                    }
                }
            }
        });
        
        if (totalWeight === 0) return "N/A";
        return (totalNormalizedScore / totalWeight).toFixed(2);
    }, []);

    const fetchProgressData = useCallback(async (empresaId, permissions) => {
        setLoading(true);
        try {
            const cycleRef = doc(db, 'Empresas', empresaId, 'evaluationCycles', cycleId);
            const usersRef = collection(db, 'users');
            const responsesRef = collection(db, 'Empresas', empresaId, 'evaluationCycles', cycleId, 'responses');
            const participantsRef = collection(db, 'Empresas', empresaId, 'evaluationCycles', cycleId, 'participants');
            
            let participantsQuery = query(participantsRef);
            let responsesQuery = query(responsesRef);

            // Filtragem de Segurança para Líderes
            if (permissions.role === 'leader' && permissions.viewableUserIds) {
                if (permissions.viewableUserIds.size === 0) { 
                    setLoading(false); 
                    setParticipants({}); 
                    setResponses([]); 
                    return; 
                }
                const viewableIdsArray = Array.from(permissions.viewableUserIds);
                // Firestore 'in' query supports max 10 items. For large teams, fetch all and filter in JS.
                // Assuming standard use case fits or implementing JS filter fallback if array > 10
                if (viewableIdsArray.length <= 10) {
                    participantsQuery = query(participantsRef, where('userId', 'in', viewableIdsArray));
                    responsesQuery = query(responsesRef, where('evaluatedUserId', 'in', viewableIdsArray));
                }
            }

            const [cycleSnap, usersSnap, responsesSnap, participantsSnap] = await Promise.all([
                getDoc(cycleRef), 
                getDocs(query(usersRef, where("empresa_id", "==", empresaId))), 
                getDocs(responsesQuery), 
                getDocs(participantsQuery)
            ]);

            if (!cycleSnap.exists()) throw new Error("Ciclo de avaliação não encontrado.");
            const cycleData = cycleSnap.data();
            setCycle(cycleData);

            const usersMap = {};
            usersSnap.forEach(userDoc => { usersMap[userDoc.id] = userDoc.data(); });
            setUsers(usersMap);

            let fetchedResponses = responsesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            let fetchedParticipants = {};
            const initialCalibrationScores = {};

            participantsSnap.forEach(pDoc => {
                const pData = pDoc.data();
                // JS Filter fallback for large leader teams
                if (permissions.role === 'leader' && permissions.viewableUserIds && !permissions.viewableUserIds.has(pData.userId)) {
                    return;
                }
                fetchedParticipants[pData.userId] = { docId: pDoc.id, ...pData };
                if (pData.finalScore !== undefined) { initialCalibrationScores[pData.userId] = pData.finalScore; }
            });

            if (permissions.role === 'leader' && permissions.viewableUserIds && permissions.viewableUserIds.size > 10) {
                 fetchedResponses = fetchedResponses.filter(r => permissions.viewableUserIds.has(r.evaluatedUserId));
            }

            setResponses(fetchedResponses);
            setParticipants(fetchedParticipants);
            setCalibrationScores(initialCalibrationScores);

            if(cycleData.form) {
                const scaleIds = [...new Set(cycleData.form.map(q => q.type).filter(type => !type.startsWith('default')))];
                const scalesMap = { 'default_0_5': { min: 0, max: 5 }, 'default_0_10': { min: 0, max: 10 } };
                if (scaleIds.length > 0) {
                    const scalesRef = collection(db, 'Empresas', empresaId, 'evaluationScales');
                    const q = query(scalesRef, where('__name__', 'in', scaleIds));
                    const scalesSnapshot = await getDocs(q);
                    scalesSnapshot.forEach(doc => { scalesMap[doc.id] = doc.data(); });
                }
                setScalesDetails(scalesMap);
            }
        } catch (err) { setError(err.message); console.error(err); } finally { setLoading(false); }
    }, [cycleId]);

    // --- CORREÇÃO DE PERMISSÃO ---
    useEffect(() => {
        const fetchPermissionsAndInitialData = async () => {
            const user = auth.currentUser;
            if (!user) { navigate('/login'); return; }
            setCurrentUserId(user.uid);
            try {
                const userDocSnap = await getDoc(doc(db, 'users', user.uid));
                if (!userDocSnap.exists() || !userDocSnap.data().empresa_id) { throw new Error("ID da empresa ou perfil do usuário não configurado."); }
                
                const userData = userDocSnap.data();
                const empresaId = userData.empresa_id;
                setAdminEmpresaId(empresaId);
                
                let permissions = { role: 'user', viewableUserIds: null };
                
                if (userData.departamento === 'People') {
                    permissions.role = 'people';
                } else {
                    // VERIFICAÇÃO CORRETA: Usa o campo do perfil do usuário
                    if (userData.canViewSubordinates === true) {
                        const orgNodesRef = collection(db, 'Empresas', empresaId, 'organizationChartNodes');
                        const qLeaderNode = query(orgNodesRef, where("userId", "==", user.uid));
                        const leaderNodeSnap = await getDocs(qLeaderNode);
                        
                        if (!leaderNodeSnap.empty) {
                            permissions.role = 'leader';
                            const leaderNodeId = leaderNodeSnap.docs[0].id;
                            const subordinateIds = new Set();
                            const nodesToProcess = [leaderNodeId], processedNodes = new Set();
                            
                            while (nodesToProcess.length > 0) {
                                const currentParentId = nodesToProcess.shift();
                                if (processedNodes.has(currentParentId)) continue;
                                processedNodes.add(currentParentId);
                                const qChildren = query(orgNodesRef, where("parentId", "==", currentParentId));
                                const childrenSnapshot = await getDocs(qChildren);
                                childrenSnapshot.forEach(childDoc => {
                                    if (childDoc.data().userId) subordinateIds.add(childDoc.data().userId);
                                    nodesToProcess.push(childDoc.id);
                                });
                            }
                            permissions.viewableUserIds = subordinateIds;
                        }
                    }
                }
                setCurrentUserPermissions(permissions);
                await fetchProgressData(empresaId, permissions);
            } catch (err) { setError(err.message); setLoading(false); }
        };
        fetchPermissionsAndInitialData();
    }, [navigate, fetchProgressData]);

    const nineBoxData = useMemo(() => {
        if (!cycle?.is9BoxEnabled || !responses || !cycle.form || !participants) return null;
        let relevantParticipantIds = Object.keys(participants);
        if (filterDepartment) {
            const deptUserIds = Object.keys(users).filter(id => users[id].departamento === filterDepartment.value);
            relevantParticipantIds = relevantParticipantIds.filter(id => deptUserIds.includes(id));
        }
        const getCategoryIndex = (score, baseScale) => {
            const percentage = (score / baseScale) * 100;
            if (percentage <= 65) return 0;
            if (percentage <= 90) return 1;
            return 2;
        };
        return relevantParticipantIds.map(evaluatedId => {
            let sourceResponse = responses.find(r => r.evaluatedUserId === evaluatedId && r.relationship === 'manager' && r.status === 'submitted');
            let source = 'manager';
            if (!sourceResponse) {
                sourceResponse = responses.find(r => r.evaluatedUserId === evaluatedId && r.relationship === 'self' && r.status === 'submitted');
                source = 'self';
            }
            if (!sourceResponse) return null;
            const performanceScore = parseFloat(calculateWeightedScore(sourceResponse.answers, cycle.form, scalesDetails, cycle.baseScale, { axis: 'performance' }));
            const potentialScore = parseFloat(calculateWeightedScore(sourceResponse.answers, cycle.form, scalesDetails, cycle.baseScale, { axis: 'potential' }));
            if (isNaN(performanceScore) || isNaN(potentialScore)) return null;
            return { userId: evaluatedId, performanceScore: performanceScore.toFixed(2), potentialScore: potentialScore.toFixed(2), performanceCategoryIndex: getCategoryIndex(performanceScore, cycle.baseScale), potentialCategoryIndex: getCategoryIndex(potentialScore, cycle.baseScale), source: source };
        }).filter(Boolean);
    }, [cycle, responses, participants, scalesDetails, calculateWeightedScore, filterDepartment, users]);

    const questionAverages = useMemo(() => {
        if (!cycle?.form || responses.length === 0) return {};
        const questionData = {};
        cycle.form.forEach(q => { questionData[q.questionId] = { byRelationship: {}, totalScores: [], totalCount: 0 }; });
        const submittedResponses = responses.filter(r => r.status === 'submitted' && r.answers);
        submittedResponses.forEach(res => {
            const relationship = res.relationship || 'other';
            Object.entries(res.answers).forEach(([qId, answer]) => {
                if (questionData[qId] && answer.score !== undefined) {
                    const score = parseFloat(answer.score);
                    if (!isNaN(score)) {
                        if (!questionData[qId].byRelationship[relationship]) {
                            questionData[qId].byRelationship[relationship] = { scores: [], sum: 0, count: 0 };
                        }
                        questionData[qId].byRelationship[relationship].scores.push(score);
                        questionData[qId].byRelationship[relationship].sum += score;
                        questionData[qId].byRelationship[relationship].count++;
                    }
                }
            });
        });
        Object.values(questionData).forEach(data => {
            Object.entries(data.byRelationship).forEach(([rel, relData]) => { data.byRelationship[rel] = relData.sum / relData.count; });
        });
        return questionData;
    }, [cycle, responses]);

    const dashboardDisplayData = useMemo(() => {
        if (!responses || !participants) return { participants: {}, responses: [] };
        if (filterDepartment) {
            const deptUserIds = Object.keys(users).filter(id => users[id].departamento === filterDepartment.value);
            const filteredParticipants = Object.keys(participants).filter(id => deptUserIds.includes(id)).reduce((obj, key) => { obj[key] = participants[key]; return obj; }, {});
            const filteredResponses = responses.filter(r => deptUserIds.includes(r.evaluatedUserId));
            return { participants: filteredParticipants, responses: filteredResponses };
        }
        return { participants, responses };
    }, [filterDepartment, participants, responses, users]);

    const dashboardStats = useMemo(() => {
        const { participants: displayParticipants, responses: displayResponses } = dashboardDisplayData;
        const total = displayResponses.length;
        if (total === 0) return { total: 0, completed: 0, percentage: 0, averageFinalScore: "N/A", participantCount: 0 };
        const completed = displayResponses.filter(r => r.status === 'submitted').length;
        const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
        const scores = Object.values(displayParticipants).map(p => p.finalScore).filter(s => s !== undefined && s !== null);
        const averageFinalScore = scores.length === 0 ? "N/A" : (scores.reduce((acc, curr) => acc + Number(curr), 0) / scores.length).toFixed(2);
        return { total, completed, percentage, averageFinalScore, participantCount: Object.keys(displayParticipants).length };
    }, [dashboardDisplayData]);

    const filteredAndGroupedData = useMemo(() => {
        if (!responses || !participants || !users) return {};
        let visibleParticipantIds = Object.keys(participants);
        if (filterDepartment) {
            visibleParticipantIds = visibleParticipantIds.filter(id => users[id]?.departamento === filterDepartment.value);
        }
        if (filterFinalScoreStatus?.value === 'with_score') {
            visibleParticipantIds = visibleParticipantIds.filter(id => participants[id]?.finalScore != null);
        } else if (filterFinalScoreStatus?.value === 'without_score') {
            visibleParticipantIds = visibleParticipantIds.filter(id => participants[id]?.finalScore == null);
        }
        const visibleParticipantIdSet = new Set(visibleParticipantIds);
        const filteredResponses = responses.filter(res => {
            if (!visibleParticipantIdSet.has(res.evaluatedUserId)) return false;
            const status = res.status === 'submitted' ? 'submitted' : 'pending';
            return (!filterEvaluated || res.evaluatedUserId === filterEvaluated.value) &&
                   (!filterEvaluator || res.evaluatorUserId === filterEvaluator.value) &&
                   (!filterStatus || status === filterStatus.value);
        });
        return filteredResponses.reduce((acc, res) => {
            const evaluatedId = res.evaluatedUserId;
            if (!acc[evaluatedId]) acc[evaluatedId] = [];
            acc[evaluatedId].push(res);
            return acc;
        }, {});
    }, [responses, users, participants, filterEvaluated, filterEvaluator, filterStatus, filterDepartment, filterFinalScoreStatus]);

    const handleExportToExcel = () => {
        setIsExporting(true);
        const getRelationshipLabel = (relationship) => {
            const labels = { self: 'Autoavaliação', manager: 'Gestor', peer: 'Par', direct_report: 'Liderado' };
            return labels[relationship] || relationship;
        };
        const baseScale = cycle?.baseScale || 10;
        const allVisibleResponses = Object.values(filteredAndGroupedData).flat();
        const dataForExport = allVisibleResponses.map(res => {
            const evaluatedId = res.evaluatedUserId;
            const nineBoxInfo = nineBoxData?.find(d => d.userId === evaluatedId);
            const finalWeightedScore = calculateWeightedScore(res.answers, cycle.form, scalesDetails, baseScale);
            const originalWeightedScore = calculateWeightedScore(res.answers, cycle.form, scalesDetails, baseScale, { useOriginal: true });
            const rowData = {
                'Avaliador': users[res.evaluatorUserId]?.name || 'N/A',
                'Avaliado': users[evaluatedId]?.name || 'N/A',
                'Departamento': users[evaluatedId]?.departamento || 'N/A',
                'Relação': getRelationshipLabel(res.relationship),
                'Status': res.status === 'submitted' ? 'Concluído' : 'Pendente',
                'Data de Envio': res.submittedAt ? res.submittedAt.toDate().toLocaleString('pt-BR') : '',
                [`Nota Ponderada Original (Base ${baseScale})`]: originalWeightedScore,
                [`Nota Ponderada Final (Base ${baseScale})`]: finalWeightedScore,
                'Nota Final (Consolidada)': participants[evaluatedId]?.finalScore ?? '',
                'Nota Desempenho (9-Box)': nineBoxInfo?.performanceScore || '',
                'Nota Potencial (9-Box)': nineBoxInfo?.potentialScore || '',
            };
            if (cycle && cycle.form) {
                cycle.form.forEach((question) => {
                    const qId = question.questionId;
                    const answerObj = res.answers ? res.answers[qId] : null;
                    rowData[`Q: ${question.text} (Nota Original)`] = answerObj?.score ?? '';
                    rowData[`Q: ${question.text} (Nota Calibrada)`] = answerObj?.calibratedScore ?? '';
                    rowData[`Q: ${question.text} (Justificativa Original)`] = answerObj?.justification || '';
                    rowData[`Q: ${question.text} (Justificativa Calibrada)`] = answerObj?.calibrationJustification || '';
                });
            }
            return rowData;
        });
        if (dataForExport.length === 0) { alert("Não há dados para exportar com os filtros atuais."); setIsExporting(false); return; }
        const worksheet = XLSX.utils.json_to_sheet(dataForExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Respostas');
        try {
            const colWidths = Object.keys(dataForExport[0]).map(key => ({ wch: Math.max(...dataForExport.map(item => (item[key] ?? '').toString().length), key.length) + 2 }));
            worksheet['!cols'] = colWidths;
        } catch(e) { console.error("Could not set column widths", e); }
        XLSX.writeFile(workbook, `Avaliacao_${cycle?.name.replace(/ /g, '_') || 'Progresso'}.xlsx`);
        setIsExporting(false);
    };

    const handleViewResponse = (response) => { setSelectedResponseData(response); setCalibratedAnswers(JSON.parse(JSON.stringify(response.answers || {}))); setIsModalOpen(true); };
    const closeModal = () => { setIsModalOpen(false); setSelectedResponseData(null); setIsCalibrating(false); setCalibratedAnswers({}); };
    const handleAnswerCalibrationChange = (questionId, field, value, max) => { setCalibratedAnswers(prev => { const updatedAnswers = { ...prev }; const currentAnswer = { ...(updatedAnswers[questionId] || {}) }; if (field === 'calibratedScore') { let score = value === '' ? undefined : parseFloat(value); if (score !== undefined && max !== undefined && score > max) score = max; if (!isNaN(score)) { currentAnswer.calibratedScore = score; } else { delete currentAnswer.calibratedScore; } } else if (field === 'calibrationJustification') { currentAnswer.calibrationJustification = value; if (currentAnswer.calibratedScore === undefined && value.trim() !== '') { currentAnswer.calibratedScore = currentAnswer.score; } } updatedAnswers[questionId] = currentAnswer; return updatedAnswers; }); };
    const handleRemoveCalibration = (questionId) => { setCalibratedAnswers(prev => { const updatedAnswers = { ...prev }; if (updatedAnswers[questionId]) { delete updatedAnswers[questionId].calibratedScore; delete updatedAnswers[questionId].calibrationJustification; } return updatedAnswers; }); };
    const handleSaveQuestionCalibration = async () => { if (!selectedResponseData || !adminEmpresaId) return; const finalAnswers = JSON.parse(JSON.stringify(calibratedAnswers)); for (const qId in finalAnswers) { const answer = finalAnswers[qId]; const isScoreUndefined = answer.calibratedScore === undefined || answer.calibratedScore === null; const isJustificationEmpty = !answer.calibrationJustification || answer.calibrationJustification.trim() === ''; if (isScoreUndefined && isJustificationEmpty) { delete answer.calibratedScore; delete answer.calibrationJustification; } } const responseRef = doc(db, 'Empresas', adminEmpresaId, 'evaluationCycles', cycleId, 'responses', selectedResponseData.id); try { await updateDoc(responseRef, { answers: finalAnswers }); alert("Calibração salva com sucesso!"); setResponses(prevResponses => prevResponses.map(res => res.id === selectedResponseData.id ? { ...res, answers: finalAnswers } : res )); setSelectedResponseData(prev => ({...prev, answers: finalAnswers})); setIsCalibrating(false); } catch(error) { console.error("Erro ao salvar calibração de perguntas: ", error); alert("Falha ao salvar a calibração."); } };
    const handleCalibrationScoreChange = (userId, score) => { setCalibrationScores(prev => ({ ...prev, [userId]: score })); };
    const handleSaveCalibration = async (userId) => { if (participants[userId]?.status === 'delivered') { alert("Não é possível alterar a nota pois o resultado já foi entregue."); return; } const finalScore = calibrationScores[userId]; if (finalScore === undefined || finalScore === '') { alert("Por favor, insira uma nota final."); return; } const participantDocId = participants[userId]?.docId; if (!participantDocId) { alert("Erro: Participante não encontrado."); return; } const participantRef = doc(db, 'Empresas', adminEmpresaId, 'evaluationCycles', cycleId, 'participants', participantDocId); try { await updateDoc(participantRef, { finalScore: Number(finalScore) }); setParticipants(prev => ({ ...prev, [userId]: { ...prev[userId], finalScore: Number(finalScore) } })); alert("Nota de calibração salva com sucesso!"); } catch (error) { console.error("Erro ao salvar calibração:", error); alert("Falha ao salvar a nota."); } };
    const handleDeliverResults = async (userId) => { if (currentUserPermissions.role !== 'people') { alert("Apenas o departamento de People pode entregar os resultados."); return; } if (!window.confirm("Tem certeza que deseja entregar o resultado para este colaborador? Ele poderá visualizar o feedback e será notificado após esta ação.")) return; const participantDocId = participants[userId]?.docId; if (!participantDocId) { alert("Erro: Participante não encontrado."); return; } const participantRef = doc(db, 'Empresas', adminEmpresaId, 'evaluationCycles', cycleId, 'participants', participantDocId); try { await updateDoc(participantRef, { status: 'delivered', notificationRead: false }); setParticipants(prev => ({ ...prev, [userId]: { ...prev[userId], status: 'delivered' } })); alert("Resultado entregue e usuário notificado com sucesso!"); } catch (error) { console.error("Erro ao entregar resultado:", error); alert("Falha ao entregar o resultado."); } };
    const handleDeliverAllResults = async () => { if (currentUserPermissions.role !== 'people') return; const pendingParticipants = Object.values(participants).filter(p => p.status !== 'delivered' && p.userId !== currentUserId); if (pendingParticipants.length === 0) { alert("Não há resultados pendentes de outros colaboradores para entregar."); return; } if (!window.confirm(`Você tem certeza que deseja entregar os resultados para ${pendingParticipants.length} colaborador(es)? Esta ação não pode ser desfeita.`)) return; try { const batch = writeBatch(db); pendingParticipants.forEach(participant => { const participantRef = doc(db, 'Empresas', adminEmpresaId, 'evaluationCycles', cycleId, 'participants', participant.docId); batch.update(participantRef, { status: 'delivered', notificationRead: false }); }); await batch.commit(); const updatedParticipants = { ...participants }; pendingParticipants.forEach(p => { updatedParticipants[p.userId] = { ...updatedParticipants[p.userId], status: 'delivered' }; }); setParticipants(updatedParticipants); alert(`${pendingParticipants.length} resultado(s) entregue(s) com sucesso!`); } catch (error) { console.error("Erro ao entregar todos os resultados:", error); alert("Ocorreu um erro ao tentar entregar os resultados em massa."); } };
    const handleSendRemindersNow = async () => { if (!window.confirm("Disparar e-mails de lembrete para todos os colaboradores com avaliações pendentes neste ciclo?")) return; setIsSubmittingReminder(true); setReminderPanelMessage({ type: 'info', text: 'Enviando lembretes...' }); try { const user = auth.currentUser; if (!user) throw new Error("Usuário não autenticado."); const token = await user.getIdToken(); const functionUrl = `https://southamerica-east1-hubgestao1.cloudfunctions.net/sendEvaluationReminders`; const response = await fetch(functionUrl, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ data: { cycleId } }) }); if (!response.ok) { const errorData = await response.json(); throw new Error(errorData.error.message || 'Falha na resposta do servidor.'); } const result = await response.json(); setReminderPanelMessage({ type: 'success', text: result.data.message }); } catch (error) { console.error("Erro ao disparar lembretes:", error); setReminderPanelMessage({ type: 'error', text: `Falha ao disparar: ${error.message}` }); } finally { setIsSubmittingReminder(false); } };
    const handleScheduleReminder = async (e) => { e.preventDefault(); const date = new Date(reminderDateTime); if (isNaN(date.getTime()) || date <= new Date()) { setReminderPanelMessage({ type: 'error', text: "Por favor, selecione uma data e hora futuras." }); return; } if (!window.confirm(`Agendar o envio de lembretes para ${date.toLocaleString('pt-BR')}?`)) return; setIsSubmittingReminder(true); setReminderPanelMessage(null); try { const remindersRef = collection(db, 'Empresas', adminEmpresaId, 'evaluationReminders'); await addDoc(remindersRef, { cycleId: cycleId, empresaId: adminEmpresaId, scheduledFor: Timestamp.fromDate(date), status: 'scheduled', createdBy: currentUserId, createdAt: Timestamp.now() }); setReminderPanelMessage({ type: 'success', text: `Lembretes agendados com sucesso!` }); setShowReminderScheduler(false); setReminderDateTime(''); } catch (error) { console.error("Erro ao agendar lembretes:", error); setReminderPanelMessage({ type: 'error', text: `Falha ao agendar: ${error.message}` }); } finally { setIsSubmittingReminder(false); } };

    const userOptions = useMemo(() => Object.entries(users).map(([id, data]) => ({ value: id, label: data.name })), [users]);
    const statusOptions = [{ value: 'pending', label: 'Pendente' }, { value: 'submitted', label: 'Concluído' }];
    const departmentOptions = useMemo(() => { if (!users) return []; const depts = Object.values(users).map(u => u.departamento).filter(Boolean); return [...new Set(depts)].map(dept => ({ value: dept, label: dept })); }, [users]);
    const finalScoreStatusOptions = [
        { value: 'all', label: 'Status da Nota (Todos)' },
        { value: 'with_score', label: 'Com Nota Final' },
        { value: 'without_score', label: 'Sem Nota Final' }
    ];
    const getRelationshipIcon = (relationship) => { const icons = { self: 'fa-user', manager: 'fa-user-tie', peer: 'fa-users', direct_report: 'fa-sitemap' }; return `fas ${icons[relationship] || 'fa-question-circle'}`; };
    const pendingResultsCount = useMemo(() => { if (!participants || !currentUserId) return 0; return Object.values(participants).filter(p => p.status !== 'delivered' && p.userId !== currentUserId).length; }, [participants, currentUserId]);

    if (loading) return <div className="progress-dashboard"><div className="loading-spinner"></div><p>Carregando painel de progresso...</p></div>;
    if (error) return <div className="progress-dashboard"><p className="error-message">{error}</p></div>;

    return (
        <div className="progress-dashboard">
            <div className="progress-header">
                <div>
                    <h2><i className="fas fa-tasks" ></i> Progresso do Ciclo: {cycle?.name}</h2>
                    <p className="header-subtitle">
                        {currentUserPermissions.role === 'leader' ? 'Acompanhando o progresso da sua equipe.' :
                         filterDepartment ? `Analisando Departamento: ${filterDepartment.label}` : 'Acompanhe os resultados e a evolução das avaliações em tempo real.'}
                    </p>
                </div>
                <div className="header-actions">
                    {currentUserPermissions.role === 'people' && (
                        <>
                            <button className="btn btn-secondary" onClick={handleSendRemindersNow} disabled={isSubmittingReminder}>
                                <FaPaperPlane /> Lembretes
                            </button>
                            <button className={`btn ${showReminderScheduler ? 'btn-light' : 'btn-outline-primary'}`} onClick={() => setShowReminderScheduler(!showReminderScheduler)} disabled={isSubmittingReminder}>
                                <FaClock /> Agendar Lembrete
                            </button>
                        </>
                    )}
                    <button onClick={handleExportToExcel} className="btn-export" disabled={isExporting || responses.length === 0}>
                        <i className="fas fa-file-excel"></i>
                        {isExporting ? 'Exportando...' : 'Exportar'}
                    </button>
                    <button onClick={() => navigate('/avaliacoes')} className="btn-back">
                        <i className="fas fa-arrow-left"></i> Voltar
                    </button>
                    {reminderPanelMessage && (<div className={`feedback-message ${reminderPanelMessage.type} global-feedback`}>{reminderPanelMessage.text}</div>)}
                </div>
            </div>

            {showReminderScheduler && currentUserPermissions.role === 'people' && (
                <div className="survey-management-panel reminders-panel">
                    <div className="panel-header"><h6>Agendar Envio de Lembretes</h6><p>O e-mail será enviado para todos os colaboradores com avaliações pendentes na data e hora escolhidas.</p></div>
                    <div className="panel-body">
                        <div className={`scheduler-form visible`}>
                            <form onSubmit={handleScheduleReminder}>
                                <div className="form-group"><label htmlFor="reminder-datetime">Agendar envio para:</label><input id="reminder-datetime" type="datetime-local" className="form-control" value={reminderDateTime} onChange={(e) => setReminderDateTime(e.target.value)} required /></div>
                                <button type="submit" className="btn btn-success w-100" disabled={isSubmittingReminder}><FaCalendarCheck /> {isSubmittingReminder ? 'Agendando...' : 'Confirmar Agendamento'}</button>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            <div className="dashboard-kpis">
                <KPI icon="fa-users" label="Total de Participantes" value={dashboardStats.participantCount} />
                <KPI icon="fa-check-circle" label="Taxa de Conclusão" value={dashboardStats.percentage} unit="%" />
                <KPI icon="fa-star-half-alt" label="Média Nota Final" value={dashboardStats.averageFinalScore} />
            </div>

            <div className="main-overview-section">
                <div className="progress-overview">
                    <h3>Progresso Geral {filterDepartment ? `(${filterDepartment.label})` : ''}</h3>
                    <div className="progress-donut-container"><div className="progress-donut" style={{ '--progress-percent': `${dashboardStats.percentage}%` }}><div className="donut-center"><span className="percentage">{dashboardStats.percentage}%</span><span className="label">Concluído</span></div></div></div>
                    <div className="overview-details"><span className="completed-count">{dashboardStats.completed}</span><span className="total-count"> / {dashboardStats.total} Avaliações</span></div>
                </div>
                {cycle?.is9BoxEnabled && nineBoxData && (<NineBoxPreview data={nineBoxData} onExpand={() => setIsNineBoxModalOpen(true)} />)}
            </div>

            <div className="individual-results-section">
                <div className="section-header-with-controls">
                    <div className="section-header"><h3><i className="fas fa-user-check"></i> Resultados Individuais</h3><p>Filtre e analise as respostas e notas de cada participante.</p></div>
                    <div className="view-mode-controls">
                         {currentUserPermissions.role === 'people' && (<button onClick={handleDeliverAllResults} className="btn-success" disabled={pendingResultsCount === 0} title={pendingResultsCount === 0 ? "Não há resultados pendentes para entregar" : `Entregar ${pendingResultsCount} resultados pendentes`}><i className="fas fa-paper-plane"></i>Entregar Todos</button>)}
                        <button onClick={() => setViewMode('card')} className={`btn-view-mode ${viewMode === 'card' ? 'active' : ''}`} title="Visualização em Cards"><i className="fas fa-th-large"></i></button>
                        <button onClick={() => setViewMode('list')} className={`btn-view-mode ${viewMode === 'list' ? 'active' : ''}`} title="Visualização em Lista"><i className="fas fa-list"></i></button>
                    </div>
                </div>

                <div className="filter-controls">
                    <div className="filter-group"><label>Filtrar por Avaliado</label><Select styles={customSelectStyles} options={userOptions} isClearable value={filterEvaluated} onChange={setFilterEvaluated} placeholder="Selecione..." /></div>
                    <div className="filter-group"><label>Filtrar por Departamento</label><Select styles={customSelectStyles} options={departmentOptions} isClearable value={filterDepartment} onChange={setFilterDepartment} placeholder="Selecione..." /></div>
                    <div className="filter-group"><label>Filtrar por Avaliador</label><Select styles={customSelectStyles} options={userOptions} isClearable value={filterEvaluator} onChange={setFilterEvaluator} placeholder="Selecione..." /></div>
                    <div className="filter-group"><label>Status da Resposta</label><Select styles={customSelectStyles} options={statusOptions} isClearable value={filterStatus} onChange={setFilterStatus} placeholder="Selecione..." /></div>
                    <div className="filter-group"><label>Status da Nota Final</label><Select styles={customSelectStyles} options={finalScoreStatusOptions} value={filterFinalScoreStatus} onChange={setFilterFinalScoreStatus} placeholder="Selecione..." /></div>
                </div>

                {viewMode === 'card' && (<div className="progress-grid">{filteredAndGroupedData && Object.keys(filteredAndGroupedData).length > 0 ? (Object.entries(filteredAndGroupedData).map(([evaluatedId, evaluations]) => { const baseScale = cycle?.baseScale || 10; const participantData = participants[evaluatedId]; const isDelivered = participantData?.status === 'delivered'; const calculateSummaryScores = (relationship) => { const response = evaluations.find(e => e.relationship === relationship && e.status === 'submitted'); if (!response) return { final: "N/A", original: "N/A" }; const finalScore = calculateWeightedScore(response.answers, cycle.form, scalesDetails, baseScale); const originalScore = calculateWeightedScore(response.answers, cycle.form, scalesDetails, baseScale, { useOriginal: true }); return { final: finalScore, original: originalScore }; }; const selfScores = calculateSummaryScores('self'); const managerScores = calculateSummaryScores('manager'); return (<div key={evaluatedId} className="progress-card"><div className="card-header">{users[evaluatedId]?.photoURL ? ( <img src={users[evaluatedId]?.photoURL} alt={users[evaluatedId]?.name} className="user-avatar" />) : (<div className="user-avatar-placeholder">{users[evaluatedId]?.name?.charAt(0) || '?'}</div>)}<h3>{users[evaluatedId]?.name || 'Usuário Desconhecido'}</h3></div><div className="evaluation-list">{evaluations.map(res => (<div key={res.id} className="evaluation-item"><div className="evaluator-info"><i className={getRelationshipIcon(res.relationship)} title={res.relationship}></i><span>{users[res.evaluatorUserId]?.name || 'Avaliador desconhecido'}</span></div><div className="actions">{res.status === 'submitted' ? (<button onClick={() => handleViewResponse(res)} className="btn-view-response">Ver Respostas</button>) : (<span className="status-badge status-pending">Pendente</span>)}</div></div>))}</div><div className="calibration-panel"><h4>Resultados (Base {baseScale}) e Calibração</h4><div className="score-summary"><div className="score-item"><span className="label">Autoavaliação</span><div className="score-display"><span className="score">{selfScores.final !== "N/A" ? selfScores.final : '-'}</span>{selfScores.final !== selfScores.original && selfScores.original !== "N/A" && (<span className="score-original">(era {selfScores.original})</span>)}</div></div><div className="score-item"><span className="label">Nota do Gestor</span><div className="score-display"><span className="score">{managerScores.final !== "N/A" ? managerScores.final : '-'}</span>{managerScores.final !== managerScores.original && managerScores.original !== "N/A" && (<span className="score-original">(era {managerScores.original})</span>)}</div></div></div>{currentUserPermissions.role === 'people' && currentUserId !== evaluatedId ? (<div className="calibration-input-group"><label htmlFor={`final-score-${evaluatedId}`}>Nota Final (Consolidada):</label><input type="number" id={`final-score-${evaluatedId}`} step="0.1" max={baseScale} value={calibrationScores[evaluatedId] ?? ''} onChange={(e) => handleCalibrationScoreChange(evaluatedId, e.target.value)} placeholder="0.0" disabled={isDelivered} />{!isDelivered && (<button className="btn btn-primary" onClick={() => handleSaveCalibration(evaluatedId)}>Salvar</button>)}</div>) : (<div className="final-score-display-only"><strong>Nota Final: </strong><span>{calibrationScores[evaluatedId] ?? 'N/A'}</span></div>)}{currentUserPermissions.role === 'people' && currentUserId !== evaluatedId && (<div className="delivery-section">{isDelivered ? (<span className="status-delivered"><i className="fas fa-check-circle"></i> Resultado Entregue</span>) : (<button className="btn-deliver" onClick={() => handleDeliverResults(evaluatedId)}><i className="fas fa-paper-plane"></i> Entregar Resultado</button>)}</div>)}</div></div>)})) : ( <div className="no-results"><i className="fas fa-info-circle"></i> Nenhum registro encontrado.</div> )}</div>)}
                {viewMode === 'list' && (<div className="calibration-list-view"><table className="calibration-table"><thead><tr><th>Colaborador(a)</th><th>Autoavaliação</th><th>Nota do Gestor</th><th>Nota Final Consolidada</th><th>Ação</th></tr></thead><tbody>{filteredAndGroupedData && Object.keys(filteredAndGroupedData).length > 0 ? (Object.entries(filteredAndGroupedData).map(([evaluatedId, evaluations]) => { const baseScale = cycle?.baseScale || 10; const isDelivered = participants[evaluatedId]?.status === 'delivered'; const calculateSummaryScores = (relationship) => { const response = evaluations.find(e => e.relationship === relationship && e.status === 'submitted'); if (!response) return { final: "N/A" }; return { final: calculateWeightedScore(response.answers, cycle.form, scalesDetails, baseScale) }; }; const selfScores = calculateSummaryScores('self'); const managerScores = calculateSummaryScores('manager'); return (<tr key={evaluatedId}><td><div className="user-cell">{users[evaluatedId]?.photoURL ? ( <img src={users[evaluatedId]?.photoURL} alt={users[evaluatedId]?.name} className="user-avatar-small" />) : (<div className="user-avatar-placeholder-small">{users[evaluatedId]?.name?.charAt(0) || '?'}</div>)}<span>{users[evaluatedId]?.name || 'Usuário Desconhecido'}</span></div></td><td data-label="Autoavaliação">{selfScores.final}</td><td data-label="Nota do Gestor">{managerScores.final}</td><td data-label="Nota Final"><input type="number" className="calibration-score-input-list" step="0.1" max={baseScale} value={calibrationScores[evaluatedId] ?? ''} onChange={(e) => handleCalibrationScoreChange(evaluatedId, e.target.value)} placeholder="-" disabled={isDelivered || currentUserId === evaluatedId || currentUserPermissions.role !== 'people'} /></td><td>{!isDelivered && currentUserId !== evaluatedId && currentUserPermissions.role === 'people' && (<button className="btn btn-primary btn-save-list" onClick={() => handleSaveCalibration(evaluatedId)}>Salvar</button>)}</td></tr>); })) : (<tr><td colSpan="5" className="no-results"><i className="fas fa-info-circle"></i> Nenhum registro encontrado.</td></tr>)}</tbody></table></div>)}
            </div>

            <QuestionAnalytics questions={cycle?.form} averages={questionAverages} baseScale={cycle?.baseScale || 10}/>
            
            {isNineBoxModalOpen && nineBoxData && (<NineBoxGrid data={nineBoxData} users={users} cycleName={cycle?.name || 'ciclo'} onClose={() => setIsNineBoxModalOpen(false)} />)}
            
            {isModalOpen && selectedResponseData && (
                <div className="modal-backdrop" onClick={closeModal}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h4>Respostas de {users[selectedResponseData.evaluatorUserId]?.name}</h4>
                            <div>
                                {!isCalibrating && currentUserPermissions.role === 'people' && currentUserId !== selectedResponseData.evaluatedUserId && participants[selectedResponseData.evaluatedUserId]?.status !== 'delivered' && (
                                    <button onClick={() => setIsCalibrating(true)} className="btn btn-secondary">Calibrar Respostas</button>
                                )}
                                <button onClick={closeModal} className="close-button">&times;</button>
                            </div>
                        </div>
                        <div className="modal-body">
                            <div className="modal-score">
                                <span>Nota Ponderada Final: {calculateWeightedScore(selectedResponseData.answers, cycle.form, scalesDetails, cycle?.baseScale)}</span>
                            </div>
                            {cycle && cycle.form.map((question, index) => {
                                const originalAnswerOnLoad = selectedResponseData.answers[question.questionId];
                                if (!originalAnswerOnLoad) return null;

                                const currentAnswerInState = calibratedAnswers[question.questionId];
                                const scaleForQuestion = scalesDetails[question.type];
                                const maxScore = scaleForQuestion?.max;

                                return (
                                    <div key={question.questionId} className="response-qna">
                                        <p className="question">{index + 1}. {question.text}</p>
                                        {isCalibrating ? (
                                            <div className="calibration-editing-block">
                                                <div className="calibration-input-row score">
                                                    <label htmlFor={`cal-score-${question.questionId}`}>Nota:</label>
                                                    <div className="input-with-action">
                                                        <input
                                                            type="number"
                                                            id={`cal-score-${question.questionId}`}
                                                            className="calibration-score-input"
                                                            value={currentAnswerInState?.calibratedScore ?? currentAnswerInState?.score ?? ''}
                                                            onChange={(e) => handleAnswerCalibrationChange(question.questionId, 'calibratedScore', e.target.value, maxScore)}
                                                            max={maxScore}
                                                            step="0.5"
                                                            placeholder="Nota"
                                                        />
                                                        <button onClick={() => handleRemoveCalibration(question.questionId)} className="btn-remove-calibration" title="Remover Calibração">
                                                            <i className="fas fa-trash-alt"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="calibration-input-row">
                                                    <label htmlFor={`cal-just-${question.questionId}`}>Justificativa da Calibração:</label>
                                                    <textarea
                                                        id={`cal-just-${question.questionId}`}
                                                        className="calibration-justification-input"
                                                        value={currentAnswerInState?.calibrationJustification ?? ''}
                                                        onChange={(e) => handleAnswerCalibrationChange(question.questionId, 'calibrationJustification', e.target.value)}
                                                        placeholder="Opcional: digite o motivo do ajuste..."
                                                        rows="2"
                                                    ></textarea>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="answer-container">
                                                <div className="answer-block">
                                                    <h5 className="block-title">Avaliação Original</h5>
                                                    <p><strong>Nota:</strong> <span className="score-badge original">{originalAnswerOnLoad.score ?? 'N/A'}</span></p>
                                                    <p className="justification-text"><strong>Justificativa:</strong> {originalAnswerOnLoad.justification || 'Não preenchida.'}</p>
                                                </div>

                                                {(originalAnswerOnLoad.calibratedScore !== undefined || (originalAnswerOnLoad.calibrationJustification && originalAnswerOnLoad.calibrationJustification.trim() !== '')) && (
                                                    <div className="answer-block calibrated">
                                                        <h5 className="block-title">Dados da Calibração</h5>
                                                        <p>
                                                            <strong>Nota:</strong> 
                                                            <span className="score-badge calibrated">
                                                                {originalAnswerOnLoad.calibratedScore ?? originalAnswerOnLoad.score ?? 'N/A'}
                                                            </span>
                                                        </p>
                                                        <p className="justification-text">
                                                            <strong>Justificativa:</strong> {originalAnswerOnLoad.calibrationJustification || 'Não preenchida.'}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        {isCalibrating && (
                            <div className="modal-footer">
                                <button onClick={() => setIsCalibrating(false)} className="btn btn-secondary">Cancelar</button>
                                <button onClick={handleSaveQuestionCalibration} className="btn btn-success">Salvar Alterações</button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProgressoAvaliacao;